// frontend/src/components/Events/EventMaterialsCarousel.jsx
// iOS 26/27 visual redesign based on the approved materials-carousel sketch.
// Mechanics/API/loading/pagination/modal flow are preserved.

import React, { useState, useEffect, useRef } from 'react';
import {
  Box,
  Typography,
  IconButton,
  Paper,
  Chip,
  Button,
  CircularProgress,
  Alert,
  alpha,
  useTheme
} from '@mui/material';
import {
  ChevronLeftRounded as ChevronLeftRoundedIcon,
  ChevronRightRounded as ChevronRightRoundedIcon,
  ArrowForwardRounded as ArrowForwardRoundedIcon,
  InsertDriveFileRounded as InsertDriveFileRoundedIcon,
  PictureAsPdfRounded as PictureAsPdfRoundedIcon,
  SlideshowRounded as SlideshowRoundedIcon,
  DescriptionRounded as DescriptionRoundedIcon,
  ImageRounded as ImageRoundedIcon,
  FolderCopyRounded as FolderCopyRoundedIcon,
  GridViewRounded as GridViewRoundedIcon,
  MovieRounded as MovieRoundedIcon,
  ArchiveRounded as ArchiveRoundedIcon
} from '@mui/icons-material';
import { styled } from '@mui/material/styles';
import { materialsAPI } from '../../services/eventsAPI';
import AllMaterialsModal from '../Materials/AllMaterialsModal';
import MaterialFolderModal from '../Materials/MaterialFolderModal';

/* -------------------------------------------------------------------------- */
/* Helpers                                                                    */
/* -------------------------------------------------------------------------- */

const firstValue = (...values) =>
  values.find((value) => value !== undefined && value !== null && value !== '');

const getParticipant = (material) =>
  material?.participant || material?.speaker || material?.author || null;

const getParticipantName = (material) => {
  const participant = getParticipant(material);

  if (typeof participant === 'string') return participant;

  if (participant) {
    return firstValue(
      participant?.user?.full_name,
      [
        participant?.user?.last_name,
        participant?.user?.first_name
      ].filter(Boolean).join(' '),
      participant?.full_name,
      participant?.name
    );
  }

  return firstValue(
    material?.author_name,
    material?.speaker_name,
    material?.participant_name,
    material?.owner_name,
    'Участник'
  );
};

const getProjectTitle = (material) => {
  const participant = getParticipant(material);
  const project = participant?.project || material?.project;

  return firstValue(
    project?.title,
    material?.project_title,
    ''
  );
};

const getBranchName = (material) => {
  const participant = getParticipant(material);
  const user = participant?.user || {};

  const branch = firstValue(
    user?.branch_detail,
    user?.branch
  );

  if (typeof branch === 'string') return branch;

  return firstValue(
    branch?.name,
    branch?.title,
    user?.branch_name,
    ''
  );
};

const getFileCollection = (material) => {
  const files = firstValue(
    material?.files,
    material?.documents,
    material?.attachments,
    material?.items
  );

  const uploaded = material?.uploaded_files;

  if (Array.isArray(files) && files.length) return files;
  if (Array.isArray(uploaded)) return uploaded;

  return [];
};

const getFileCount = (material) => {
  const files = getFileCollection(material);

  return Number(
    firstValue(
      material?.files_count,
      material?.documents_count,
      material?.attachments_count,
      material?.file_count,
      files.length,
      0
    )
  ) || 0;
};

const getFileType = (material) => {
  const files = getFileCollection(material);

  const directType = firstValue(
    material?.file_type,
    material?.file_extension,
    material?.extension,
    material?.format,
    files[0]?.file_type,
    files[0]?.file_extension,
    files[0]?.extension,
    files[0]?.format,
    files[0]?.filename?.split?.('.')?.pop?.(),
    files[0]?.name?.split?.('.')?.pop?.()
  );

  if (!directType) return 'FILES';

  const normalized = String(directType).replace('.', '').toUpperCase();

  const aliases = {
    'APPLICATION/PDF': 'PDF',
    'PDF': 'PDF',
    'PPT': 'PPTX',
    'PPTX': 'PPTX',
    'PRESENTATION': 'PPTX',
    'DOC': 'DOC',
    'DOCX': 'DOC',
    'DOCUMENT': 'DOC',
    'JPG': 'IMG',
    'JPEG': 'IMG',
    'PNG': 'IMG',
    'WEBP': 'IMG',
    'IMAGE': 'IMG',
    'XLS': 'XLSX',
    'XLSX': 'XLSX',
    'ZIP': 'ZIP',
    'RAR': 'ZIP',
    'VIDEO': 'VIDEO',
    'MP4': 'VIDEO',
    'MOV': 'VIDEO'
  };

  return aliases[normalized] || normalized;
};

const getFileVisual = (type, theme) => {
  const configs = {
    PDF: {
      icon: PictureAsPdfRoundedIcon,
      label: 'PDF',
      color: '#FF3B30',
      soft: '#FF3B3017'
    },
    PPTX: {
      icon: SlideshowRoundedIcon,
      label: 'PPTX',
      color: '#FF7A00',
      soft: '#FF7A0017'
    },
    DOC: {
      icon: DescriptionRoundedIcon,
      label: 'DOC',
      color: '#1677FF',
      soft: '#1677FF17'
    },
    IMG: {
      icon: ImageRoundedIcon,
      label: 'IMG',
      color: '#10B981',
      soft: '#10B98117'
    },
    XLSX: {
      icon: GridViewRoundedIcon,
      label: 'XLSX',
      color: '#22A06B',
      soft: '#22A06B17'
    },
    ZIP: {
      icon: ArchiveRoundedIcon,
      label: 'ZIP',
      color: '#8B5CF6',
      soft: '#8B5CF617'
    },
    VIDEO: {
      icon: MovieRoundedIcon,
      label: 'VIDEO',
      color: '#D946EF',
      soft: '#D946EF17'
    },
    MULTI: {
      icon: FolderCopyRoundedIcon,
      label: 'MULTI',
      color: '#64748B',
      soft: '#64748B17'
    }
  };

  return configs[type] || {
    icon: InsertDriveFileRoundedIcon,
    label: type || 'FILES',
    color: theme.palette.primary.main,
    soft: alpha(theme.palette.primary.main, 0.09)
  };
};

/* -------------------------------------------------------------------------- */
/* iOS / Glass surfaces                                                       */
/* -------------------------------------------------------------------------- */

const MaterialsSection = styled(Box)(({ theme }) => ({
  position: 'relative',
  marginTop: theme.spacing(2),
  marginBottom: theme.spacing(4),
  background: 'linear-gradient(135deg, rgba(255,255,255,0.62), rgba(255,255,255,0.42))',
  border: '1px solid rgba(255,255,255,0.78)',
  borderRadius: theme.spacing(3),
  padding: theme.spacing(2.5, 1.5, 2),
  overflow: 'visible',
  boxShadow: '0 18px 45px rgba(35,45,55,0.06), inset 0 1px 0 rgba(255,255,255,0.88)',
  backdropFilter: 'blur(12px) saturate(165%)',
  WebkitBackdropFilter: 'blur(12px) saturate(165%)'
}));

const SectionHeader = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  gap: theme.spacing(2),
  marginBottom: theme.spacing(1),
  padding: theme.spacing(0, 1.5),

  [theme.breakpoints.down('sm')]: {
    alignItems: 'flex-start'
  }
}));

const TitleRow = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: theme.spacing(1.2),
  minWidth: 0
}));

const SectionIcon = styled(Box)(({ theme }) => ({
  width: 40,
  height: 40,
  minWidth: 40,
  borderRadius: 14,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  color: theme.palette.primary.main,
  background: `linear-gradient(145deg, ${alpha('#FFFFFF', 0.96)}, ${alpha(theme.palette.primary.main, 0.08)})`,
  border: `1px solid ${alpha(theme.palette.primary.main, 0.12)}`,
  boxShadow: `0 8px 22px ${alpha(theme.palette.primary.main, 0.10)}, inset 0 1px 0 rgba(255,255,255,0.95)`
}));

const CountPill = styled(Chip)(({ theme }) => ({
  height: 34,
  borderRadius: 17,
  padding: theme.spacing(0, 0.35),
  fontWeight: 700,
  fontSize: '0.9rem',
  color: theme.palette.primary.main,
  background: alpha(theme.palette.primary.main, 0.08),
  border: `1px solid ${alpha(theme.palette.primary.main, 0.08)}`,
  '& .MuiChip-label': {
    padding: theme.spacing(0, 1.15)
  }
}));

const AllButton = styled(Button)(({ theme }) => ({
  minHeight: 42,
  padding: theme.spacing(0.8, 1.7),
  borderRadius: 22,
  textTransform: 'none',
  fontWeight: 650,
  color: theme.palette.primary.main,
  background: `linear-gradient(180deg, ${alpha('#FFFFFF', 0.94)}, ${alpha(theme.palette.primary.main, 0.06)})`,
  border: `1px solid ${alpha(theme.palette.primary.main, 0.12)}`,
  boxShadow: `0 7px 20px ${alpha(theme.palette.primary.main, 0.08)}, inset 0 1px 0 rgba(255,255,255,0.9)`,
  whiteSpace: 'nowrap',
  transition: 'transform .2s ease, box-shadow .2s ease, background .2s ease',

  '&:hover': {
    background: `linear-gradient(180deg, ${alpha('#FFFFFF', 0.98)}, ${alpha(theme.palette.primary.main, 0.10)})`,
    transform: 'translateY(-1px)',
    boxShadow: `0 10px 26px ${alpha(theme.palette.primary.main, 0.13)}`
  },

  [theme.breakpoints.down('sm')]: {
    padding: theme.spacing(0.7, 1.25),
    '& .all-button-label': {
      display: 'none'
    }
  }
}));

// Горизонтальный scroll-контейнер: карточки внутри не используют backdrop-filter,
// чтобы браузер не пересобирал стек glass-слоёв на каждом кадре прокрутки.
const ScrollContainer = styled(Box)(({ theme }) => ({
  display: 'flex',
  overflowX: 'auto',
  overflowY: 'visible',
  gap: theme.spacing(1.75),
  padding: theme.spacing(2, 3, 2.5),
  scrollBehavior: 'smooth',
  scrollbarWidth: 'thin',
  scrollbarColor: `${alpha(theme.palette.text.secondary, 0.18)} transparent`,

  '&::-webkit-scrollbar': {
    height: 6
  },
  '&::-webkit-scrollbar-track': {
    background: 'transparent'
  },
  '&::-webkit-scrollbar-thumb': {
    background: alpha(theme.palette.text.secondary, 0.16),
    borderRadius: 999
  },
  '&::-webkit-scrollbar-thumb:hover': {
    background: alpha(theme.palette.primary.main, 0.32)
  }
}));

const NavButton = styled(IconButton)(({ theme }) => ({
  position: 'absolute',
  top: '50%',
  transform: 'translateY(-58%)',
  width: 44,
  height: 44,
  zIndex: 12,
  color: theme.palette.primary.main,
  background: alpha('#FFFFFF', 0.72),
  border: `1px solid ${alpha('#FFFFFF', 0.88)}`,
  boxShadow: `0 10px 28px ${alpha('#000000', 0.10)}, inset 0 1px 0 rgba(255,255,255,0.92)`,
  transition: 'transform .22s ease, box-shadow .22s ease, background .22s ease',

  '&:hover': {
    background: alpha('#FFFFFF', 0.90),
    transform: 'translateY(-58%) scale(1.06)',
    boxShadow: `0 14px 34px ${alpha('#000000', 0.13)}, 0 0 0 4px ${alpha(theme.palette.primary.main, 0.06)}`
  }
}));

const MaterialCard = styled(Paper)(({ theme }) => ({
  position: 'relative',
  minWidth: 210,
  maxWidth: 210,
  height: 228,
  flexShrink: 0,
  borderRadius: 26,
  overflow: 'hidden',
  cursor: 'pointer',

  background: `
    radial-gradient(circle at 25% 5%, ${alpha(theme.palette.primary.main, 0.08)}, transparent 38%),
    linear-gradient(145deg, rgba(255,255,255,0.94), rgba(244,249,246,0.84))
  `,
  border: `1px solid rgba(255,255,255,0.88)`,
  boxShadow: `
    0 12px 30px rgba(24,45,36,0.07),
    inset 0 1px 0 rgba(255,255,255,0.96)
  `,
  display: 'flex',
  flexDirection: 'column',
  padding: theme.spacing(2),

  transition: 'transform .28s cubic-bezier(.22,1,.36,1), box-shadow .28s ease, background .28s ease',

  '&::before': {
    content: '""',
    position: 'absolute',
    inset: 0,
    pointerEvents: 'none',
    borderRadius: 'inherit',
    background: 'linear-gradient(135deg, rgba(255,255,255,.62), transparent 34%, transparent 72%, rgba(255,255,255,.22))'
  },

  '&:hover': {
    transform: 'translateY(-5px)',
    boxShadow: `
      0 20px 40px rgba(24,45,36,0.11),
      0 0 0 4px ${alpha(theme.palette.primary.main, 0.035)},
      inset 0 1px 0 rgba(255,255,255,0.98)
    `,
    background: `
      radial-gradient(circle at 25% 5%, ${alpha(theme.palette.primary.main, 0.12)}, transparent 40%),
      linear-gradient(145deg, rgba(255,255,255,0.98), rgba(244,249,246,0.90))
    `
  },

  '&:active': {
    transform: 'translateY(-2px) scale(.992)'
  },

  [theme.breakpoints.down('sm')]: {
    minWidth: 190,
    maxWidth: 190,
    height: 216
  }
}));

const FileVisual = styled(Box)(({ theme }) => ({
  position: 'relative',
  height: 94,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  marginBottom: theme.spacing(1.3)
}));

const FileStack = styled(Box, {
  shouldForwardProp: (prop) => prop !== 'accent' && prop !== 'soft'
})(({ accent, soft }) => ({
  position: 'relative',
  width: 94,
  height: 104,
  borderRadius: 23,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  background: `linear-gradient(145deg, rgba(255,255,255,.96), ${soft})`,
  border: '1px solid rgba(255,255,255,.92)',
  boxShadow: `0 13px 28px ${accent}22, inset 0 1px 0 rgba(255,255,255,.98)`,
  transform: 'translateX(-2px)',

  '&::before': {
    content: '""',
    position: 'absolute',
    width: 78,
    height: 88,
    left: -11,
    top: 8,
    borderRadius: 20,
    background: `linear-gradient(145deg, rgba(255,255,255,.72), ${soft})`,
    border: '1px solid rgba(255,255,255,.74)',
    transform: 'rotate(-5deg)',
    opacity: 0.82
  },

  '&::after': {
    content: '""',
    position: 'absolute',
    width: 70,
    height: 80,
    right: -12,
    top: 15,
    borderRadius: 18,
    background: `linear-gradient(145deg, rgba(255,255,255,.66), ${soft})`,
    border: '1px solid rgba(255,255,255,.70)',
    transform: 'rotate(7deg)',
    opacity: 0.76
  }
}));

const FileIconSurface = styled(Box, {
  shouldForwardProp: (prop) => prop !== 'accent' && prop !== 'soft'
})(({ accent, soft }) => ({
  position: 'relative',
  zIndex: 3,
  width: 62,
  height: 70,
  borderRadius: 17,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  color: '#FFFFFF',
  background: `linear-gradient(145deg, ${accent}, ${accent}D9)`,
  boxShadow: `0 10px 22px ${accent}45, inset 0 1px 0 rgba(255,255,255,.38)`,
  '& svg': {
    fontSize: 36,
    filter: 'drop-shadow(0 2px 3px rgba(0,0,0,.12))'
  },
  '&::after': {
    content: '""',
    position: 'absolute',
    inset: 1,
    borderRadius: 16,
    border: '1px solid rgba(255,255,255,.28)',
    pointerEvents: 'none'
  }
}));

const TypePill = styled(Box, {
  shouldForwardProp: (prop) => prop !== 'accent' && prop !== 'soft'
})(({ accent, soft }) => ({
  position: 'absolute',
  zIndex: 5,
  right: 10,
  bottom: 7,
  minWidth: 46,
  height: 28,
  padding: '0 10px',
  borderRadius: 14,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  color: accent,
  background: `linear-gradient(180deg, rgba(255,255,255,.98), ${soft})`,
  border: `1px solid ${accent}22`,
  boxShadow: `0 6px 16px ${accent}1C, inset 0 1px 0 rgba(255,255,255,.92)`,
  fontSize: '0.72rem',
  fontWeight: 800,
  letterSpacing: '.03em',
 }));

const CardTitle = styled(Typography)(({ theme }) => ({
  position: 'relative',
  zIndex: 2,
  fontSize: '1rem',
  lineHeight: 1.25,
  fontWeight: 750,
  color: theme.palette.text.primary,
  display: '-webkit-box',
  WebkitLineClamp: 2,
  WebkitBoxOrient: 'vertical',
  overflow: 'hidden',
  // Не резервируем лишнюю высоту, когда ФИО занимает только одну строку:
  // это позволяет сразу показать название филиала под именем.
  minHeight: 0
}));

const CardAuthor = styled(Typography)(({ theme }) => ({
  position: 'relative',
  zIndex: 2,
  marginTop: theme.spacing(0.55),
  fontSize: '0.82rem',
  lineHeight: 1.25,
  color: theme.palette.text.secondary,
  whiteSpace: 'nowrap',
  overflow: 'hidden',
  textOverflow: 'ellipsis'
}));

const CardFooter = styled(Box)(({ theme }) => ({
  position: 'relative',
  zIndex: 2,
  marginTop: 'auto',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  gap: theme.spacing(1)
}));

const MetaLine = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  minWidth: 0,
  gap: theme.spacing(0.55),
  color: theme.palette.text.secondary,
  fontSize: '0.76rem'
}));

const CardArrow = styled(IconButton)(({ theme }) => ({
  width: 38,
  height: 38,
  flexShrink: 0,
  borderRadius: 14,
  color: theme.palette.primary.main,
  background: 'rgba(255,255,255,.72)',
  border: '1px solid rgba(255,255,255,.90)',
  boxShadow: `0 7px 18px ${alpha(theme.palette.primary.main, 0.10)}, inset 0 1px 0 rgba(255,255,255,.94)`,
  transition: 'transform .2s ease, background .2s ease, box-shadow .2s ease',

  '&:hover': {
    background: 'rgba(255,255,255,.94)',
    transform: 'translateX(2px)',
    boxShadow: `0 9px 22px ${alpha(theme.palette.primary.main, 0.16)}`
  }
}));

const AllMaterialsCard = styled(Paper)(({ theme }) => ({
  position: 'relative',
  minWidth: 210,
  maxWidth: 210,
  height: 228,
  flexShrink: 0,
  borderRadius: 26,
  cursor: 'pointer',
  overflow: 'hidden',

  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
  gap: theme.spacing(1),

  background: `
    radial-gradient(circle at 50% 22%, ${alpha(theme.palette.primary.main, 0.10)}, transparent 42%),
    linear-gradient(145deg, rgba(255,255,255,.82), rgba(239,248,243,.70))
  `,
  border: `1px solid ${alpha(theme.palette.primary.main, 0.12)}`,
  boxShadow: `0 12px 30px ${alpha(theme.palette.common.black, 0.06)}, inset 0 1px 0 rgba(255,255,255,.94)`,
  transition: 'transform .28s cubic-bezier(.22,1,.36,1), box-shadow .28s ease',

  '&:hover': {
    transform: 'translateY(-5px)',
    boxShadow: `0 20px 40px ${alpha(theme.palette.common.black, 0.10)}, 0 0 0 4px ${alpha(theme.palette.primary.main, 0.035)}`
  }
}));

const AllMaterialsVisual = styled(Box)(({ theme }) => ({
  width: 96,
  height: 96,
  borderRadius: 28,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  color: theme.palette.primary.main,
  background: `
    linear-gradient(145deg, rgba(255,255,255,.78), ${alpha(theme.palette.primary.main, .10)})
  `,
  border: '1px solid rgba(255,255,255,.92)',
  boxShadow: `0 12px 28px ${alpha(theme.palette.primary.main, .12)}, inset 0 1px 0 rgba(255,255,255,.96)`,
  fontSize: '2.2rem',
  fontWeight: 300
}));

const EmptyState = styled(Box)(({ theme }) => ({
  minHeight: 210,
  margin: theme.spacing(1, 1.5, 2),
  borderRadius: 26,
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
  textAlign: 'center',
  background: `linear-gradient(145deg, rgba(255,255,255,.78), ${alpha(theme.palette.primary.main, .035)})`,
  border: `1px solid ${alpha(theme.palette.primary.main, .08)}`,
  boxShadow: `inset 0 1px 0 rgba(255,255,255,.9)`
}));

const LoaderContainer = styled(Box)(() => ({
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  minHeight: 210,
  width: '100%'
}));

/* -------------------------------------------------------------------------- */
/* Main component                                                             */
/* -------------------------------------------------------------------------- */

const EventMaterialsCarousel = ({
  materials = [],
  eventId,
  eventType,
  sectionId = null,
  isPlenary = false,
  totalCount = 0,
  loading = false,
  error = null,
  onLoadMore,
  hasMore = false,
  title = 'МАТЕРИАЛЫ'
}) => {
  const theme = useTheme();
  const scrollRef = useRef(null);

  const [showLeftArrow, setShowLeftArrow] = useState(false);
  const [showRightArrow, setShowRightArrow] = useState(false);
  const [allMaterialsModalOpen, setAllMaterialsModalOpen] = useState(false);
  const [folderModalOpen, setFolderModalOpen] = useState(false);
  const [selectedMaterial, setSelectedMaterial] = useState(null);
  const [allMaterials, setAllMaterials] = useState([]);
  const [allMaterialsTotal, setAllMaterialsTotal] = useState(0);
  const [allMaterialsLoading, setAllMaterialsLoading] = useState(false);
  const [allMaterialsError, setAllMaterialsError] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [hasMorePages, setHasMorePages] = useState(false);

  const displayedMaterials = materials.slice(0, 10);
  const hasMoreMaterials = materials.length > 10 || hasMore;

  useEffect(() => {
    const container = scrollRef.current;
    if (!container) return;

    let frameId = null;

    const checkScroll = () => {
      if (frameId !== null) return;

      frameId = requestAnimationFrame(() => {
        frameId = null;

        const current = scrollRef.current;
        if (!current) return;

        const nextShowLeft = current.scrollLeft > 2;
        const nextShowRight =
          current.scrollLeft <
          current.scrollWidth - current.clientWidth - 10;

        setShowLeftArrow(prev => (
          prev === nextShowLeft ? prev : nextShowLeft
        ));

        setShowRightArrow(prev => (
          prev === nextShowRight ? prev : nextShowRight
        ));
      });
    };

    checkScroll();
    window.addEventListener('resize', checkScroll, { passive: true });
    container.addEventListener('scroll', checkScroll, { passive: true });

    return () => {
      window.removeEventListener('resize', checkScroll);

      if (frameId !== null) {
        cancelAnimationFrame(frameId);
      }

      container.removeEventListener('scroll', checkScroll);
    };
  }, [displayedMaterials]);

  const scrollLeft = () => {
    if (scrollRef.current) {
      scrollRef.current.scrollBy({ left: -300, behavior: 'smooth' });
    }
  };

  const scrollRight = () => {
    if (scrollRef.current) {
      scrollRef.current.scrollBy({ left: 300, behavior: 'smooth' });
    }
  };

  const handleMaterialClick = (material) => {
    console.log('📦 Выбран материал:', material);
    setSelectedMaterial(material);
    setFolderModalOpen(true);
  };

  const handleFolderModalClose = () => {
    setFolderModalOpen(false);
    setSelectedMaterial(null);
  };

  const loadAllMaterials = async (page = 1) => {
    if (!eventId) return;

    setAllMaterialsLoading(true);
    setAllMaterialsError(null);

    try {
      let response;

      if (sectionId) {
        response = await materialsAPI.getSectionMaterials(sectionId, page, 10);
      } else if (isPlenary) {
        response = await materialsAPI.getPlenaryMaterials(eventId, page, 10);
      } else {
        response = await materialsAPI.getAllMaterialsPaginated(
          page,
          10,
          { event: eventId }
        );
      }

      console.log('📦 loadAllMaterials response:', response);

      setAllMaterials(response.results || []);
      setAllMaterialsTotal(
        response.totalCount || response.results?.length || 0
      );
      setCurrentPage(page);
      setHasMorePages(response.hasMore);
    } catch (err) {
      console.error('❌ Ошибка загрузки всех материалов:', err);
      setAllMaterialsError('Не удалось загрузить материалы');
    } finally {
      setAllMaterialsLoading(false);
    }
  };

  const handleShowAll = () => {
    setAllMaterialsModalOpen(true);
    loadAllMaterials(1);
  };

  const handlePageChange = async (page) => {
    await loadAllMaterials(page);
  };

  const renderMaterialCard = (material) => {
    const type = getFileType(material);
    const visual = getFileVisual(type, theme);
    const Icon = visual.icon;
    const authorText = getParticipantName(material);
    const branchName = getBranchName(material);
    const fileCount = getFileCount(material);

    return (
      <MaterialCard
        key={material.id}
        onClick={() => handleMaterialClick(material)}
        role="button"
        tabIndex={0}
        onKeyDown={(event) => {
          if (event.key === 'Enter' || event.key === ' ') {
            event.preventDefault();
            handleMaterialClick(material);
          }
        }}
        aria-label={`Открыть материалы: ${authorText}`}
      >
        <FileVisual>
          <FileStack accent={visual.color} soft={visual.soft}>
            <FileIconSurface accent={visual.color} soft={visual.soft}>
              <Icon />
            </FileIconSurface>
          </FileStack>

          <TypePill accent={visual.color} soft={visual.soft}>
            {visual.label}
          </TypePill>
        </FileVisual>

        <CardTitle>{authorText}</CardTitle>
        {branchName && (
          <Typography
            sx={{
              position: 'relative',
              zIndex: 2,
              mt: 0.45,
              fontSize: '0.75rem',
              lineHeight: 1.2,
              color: theme.palette.primary.main,
              whiteSpace: 'nowrap',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              fontWeight: 650
            }}
          >
            {branchName}
          </Typography>
        )}

        <CardFooter>
          <MetaLine>
            <InsertDriveFileRoundedIcon sx={{ fontSize: 17 }} />
            <span>
              {fileCount > 0
                ? `${fileCount} ${fileCount === 1 ? 'файл' : 'файла'}`
                : 'Файлы'}
            </span>
            <span>·</span>
            <span>{visual.label}</span>
          </MetaLine>

          <CardArrow
            size="small"
            aria-label="Открыть материал"
            onClick={(event) => {
              event.stopPropagation();
              handleMaterialClick(material);
            }}
          >
            <ArrowForwardRoundedIcon sx={{ fontSize: 20 }} />
          </CardArrow>
        </CardFooter>
      </MaterialCard>
    );
  };

  return (
    <>
      <MaterialsSection>
        <SectionHeader>
          <TitleRow>
            <SectionIcon>
              <FolderCopyRoundedIcon fontSize="small" />
            </SectionIcon>

            <Typography
              variant="h5"
              fontWeight={750}
              color="text.primary"
              sx={{
                letterSpacing: '-0.02em',
                whiteSpace: 'nowrap'
              }}
            >
              {title}
            </Typography>

            {totalCount > 0 && (
              <CountPill label={totalCount} />
            )}
          </TitleRow>

          {materials.length > 0 && (
            <AllButton onClick={handleShowAll}>
              <span className="all-button-label">Все материалы</span>
              <ArrowForwardRoundedIcon
                sx={{
                  ml: { xs: 0, sm: 0.6 },
                  fontSize: 20
                }}
              />
            </AllButton>
          )}
        </SectionHeader>

        <Box sx={{ position: 'relative' }}>
          {showLeftArrow && (
            <NavButton
              onClick={scrollLeft}
              sx={{ left: -2 }}
              size="small"
              aria-label="Предыдущие материалы"
            >
              <ChevronLeftRoundedIcon />
            </NavButton>
          )}

          {showRightArrow && (
            <NavButton
              onClick={scrollRight}
              sx={{ right: -2 }}
              size="small"
              aria-label="Следующие материалы"
            >
              <ChevronRightRoundedIcon />
            </NavButton>
          )}

          {loading ? (
            <LoaderContainer>
              <CircularProgress size={38} />
            </LoaderContainer>
          ) : error ? (
            <Alert
              severity="error"
              sx={{
                mx: 1.5,
                borderRadius: 3,
                background: alpha(theme.palette.error.main, 0.06),
                border: `1px solid ${alpha(theme.palette.error.main, 0.12)}`
              }}
            >
              {error}
            </Alert>
          ) : materials.length === 0 ? (
            <EmptyState>
              <SectionIcon sx={{ width: 52, height: 52, minWidth: 52, mb: 0.5 }}>
                <FolderCopyRoundedIcon />
              </SectionIcon>

              <Typography
                variant="body1"
                color="text.primary"
                sx={{ fontWeight: 750, mb: 0.4 }}
              >
                Пока нет {eventType === 'competition' ? 'конкурсантов' : 'докладчиков'}
              </Typography>

              <Typography
                variant="caption"
                color="text.secondary"
                sx={{
                  display: 'block',
                  maxWidth: 340,
                  px: 2
                }}
              >
                Когда появятся {eventType === 'competition' ? 'конкурсанты' : 'докладчики'}, их материалы будут отображаться здесь
              </Typography>
            </EmptyState>
          ) : (
            <ScrollContainer ref={scrollRef}>
              {displayedMaterials.map(renderMaterialCard)}

              {hasMoreMaterials && (
                <AllMaterialsCard onClick={handleShowAll}>
                  <AllMaterialsVisual>
                    +{Math.max(0, totalCount - 10)}
                  </AllMaterialsVisual>

                  <Typography
                    variant="body1"
                    fontWeight={750}
                    color="primary.main"
                    sx={{ mt: 0.25 }}
                  >
                    Все материалы
                  </Typography>

                  <IconButton
                    size="small"
                    aria-label="Показать все материалы"
                    sx={{
                      width: 38,
                      height: 38,
                      mt: 0.2,
                      color: 'primary.main',
                      background: 'rgba(255,255,255,.78)',
                      border: '1px solid rgba(255,255,255,.90)',
                      boxShadow: '0 7px 18px rgba(30,100,75,.10)',
                      '&:hover': {
                        background: 'rgba(255,255,255,.96)'
                      }
                    }}
                  >
                    <ArrowForwardRoundedIcon sx={{ fontSize: 20 }} />
                  </IconButton>
                </AllMaterialsCard>
              )}
            </ScrollContainer>
          )}
        </Box>
      </MaterialsSection>

      <AllMaterialsModal
        open={allMaterialsModalOpen}
        onClose={() => setAllMaterialsModalOpen(false)}
        materials={allMaterials}
        totalCount={allMaterialsTotal}
        loading={allMaterialsLoading}
        error={allMaterialsError}
        onPageChange={handlePageChange}
        currentPage={currentPage}
        hasMore={hasMorePages}
        onMaterialClick={handleMaterialClick}
      />

      <MaterialFolderModal
        open={folderModalOpen}
        onClose={handleFolderModalClose}
        material={selectedMaterial}
      />
    </>
  );
};

export default EventMaterialsCarousel;
